import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { debounce } from "lodash";
import MainBody from "../../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import AddEditPolicy from "./AddEditPolicy";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import PolicyActionButton from "./PolicyActionButton";
import Confirmation from "../../../../components/Confirmation";
import { useOrganization } from "../../../../lib/context/OrganisationContext";
import AdminFilters from "../../../../components/AdminFilters";

const Policies = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [policiesList, setPoliciesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { organizationData } = useOrganization();

  const [policyId, setPolicyId] = useState(null);
  const [showAddEditPolicy, setShowAddEditPolicy] = useState(false);
  const [showPolicyDelete, setShowPolicyDelete] = useState(false);

  const [versionId, setVersionId] = useState();
  const [sectionType, setSectionType] = useState();

  const [organizationTypeId, setOrganizationTypeId] = useState();

  const toggleShowAddEditPolicy = () => {
    if (showAddEditPolicy) {
      setEdit(false);
      setPolicyId(null);
    }
    setShowAddEditPolicy(!showAddEditPolicy);
  };

  const toggleShowDelete = () => {
    if (showPolicyDelete) {
      setPolicyId(null);
    }
    setShowPolicyDelete(!showPolicyDelete);
  };

  const fetchPolicy = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_POLICIES}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&organization_type_id=${
            organizationTypeId ? organizationTypeId : ""
          }&version_id=${versionId ? versionId : ""}&section_type=${
            sectionType ? sectionType : ""
          }`,
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const policiesList = data.data.data;
        setPoliciesList(policiesList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setPoliciesList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchParam,
      sortOrder,
      sortField,
      versionId,
      organizationTypeId,
      sectionType,
    ],
  );

  const policiesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: policiesList[i]?.id,
        id: policiesList[i]?.id,
        organization_type: policiesList[i]?.organization_type?.name
          ? policiesList[i]?.organization_type?.name
          : "",
        version_number: policiesList[i]?.version?.version_number
          ? `${policiesList[i]?.version?.version_number} - ${policiesList[i]?.version?.name}`
          : "",
        section_type: policiesList[i]?.condition?.section_type
          ? policiesList[i]?.condition?.section_type
          : "",
        condition: policiesList[i]?.condition?.risk_level
          ? policiesList[i]?.condition?.risk_level
          : "",
        policy_file: policiesList[i]?.policy_file
          ? policiesList[i]?.policy_file
          : "",
        is_used: policiesList[i]?.is_used,
        action: (
          <>
            <PolicyActionButton
              id={policiesList[i]?.id}
              setEdit={setEdit}
              setPolicyId={setPolicyId}
              toggleShowAddEditPolicy={toggleShowAddEditPolicy}
              toggleShowDelete={toggleShowDelete}
              is_used={policiesList[i]?.is_used}
              policy_file={
                policiesList[i]?.policy_file ? policiesList[i]?.policy_file : ""
              }
            />
          </>
        ),
      });
    }
    return items;
  };

  const policies = policiesGenerator(policiesList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "organization_type",
      text: "Industry Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "version_number",
      text: "Version Number",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "section_type",
      text: "Section Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "condition",
      text: "Condition",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  useEffect(() => {
    navigate(
      `?tab=policies&limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      },
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchPolicy(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchPolicy(limit, page);
  };

  // Single Policy Delete
  const handlePolicyDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ADMIN_POLICY_DELETE}/${policyId}`);
      setLoading(false);
      fetchPolicy();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            <AdminFilters
              organizationType={true}
              setOrganizationTypeId={setOrganizationTypeId}
              organizationTypeId={organizationTypeId}
              version={true}
              setVersionId={setVersionId}
              versionId={versionId}
              sectionTypeSelect={true}
              setSectionType={setSectionType}
              sectionType={sectionType}
            />
            {/* Add Policy */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Policy" : "Add Policy"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setPolicyId(null);
                setShowAddEditPolicy(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Policy</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditPolicy}
              buttonClassName={""}
              setOpen={setShowAddEditPolicy}
            >
              <AddEditPolicy
                edit={edit}
                toggleShowAddEditPolicy={toggleShowAddEditPolicy}
                id={policyId}
                orgId={
                  organizationData?.organizationId
                    ? organizationData?.organizationId
                    : ""
                }
                fetchPolicyData={() => fetchPolicy(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        innerBody={true}
        expandList={true}
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[590px]">
            <BootstrapTable
              keyField="key"
              remote
              data={policies}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Policy"}
        open={showPolicyDelete}
        setOpen={setShowPolicyDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete policy?"}
        okAction={async () => {
          handlePolicyDelete();
          toggleShowDelete();
          fetchPolicy();
        }}
        cancelAction={() => {
          setPolicyId(null);
          setShowPolicyDelete(!showPolicyDelete);
        }}
      />
    </>
  );
};

export default Policies;
